/**
 * Formatierung, die an Bedingungen und Regeln hängt.
 *
 * Bewusst getrennt von `workbook.ts`: Bedingte Formatierung (ab ExcelApi 1.6),
 * Zellformatvorlagen (1.7), Datenüberprüfung (1.8) und die Formatangaben je
 * Zelle (1.9) stammen aus deutlich höheren Anforderungssätzen als die
 * Grundangaben. Wer sie mit in die Hauptmomentaufnahme legt, riskiert, dass ein
 * fehlender Satz den gesamten Abruf mitreißt.
 *
 * Jede Gruppe wird einzeln abgefragt und liefert `null`, wenn die laufende
 * Excel-Version sie nicht hergibt. Die Prüfungen melden das über
 * `nichtPruefbar()` offen, statt zu raten.
 */

import { excelApi, supportsExcelApi } from "./capabilities";
import { columnLetter, isExcelAvailable, ExcelUnavailableError, splitAddress } from "./workbook";

/** Obergrenzen, damit eine große Arbeitsmappe den Abruf nicht sprengt. */
const MAX_SPALTEN = 60;
const MAX_ZEILEN = 200;

export interface ConditionalFormatInfo {
  sheetName: string;
  /** Bereich, für den die Regel gilt, z. B. "B2:B20". */
  address: string | null;
  /**
   * "CellValue", "ColorScale", "DataBar", "IconSet", "TopBottom",
   * "ContainsText", "Custom" oder "PresetCriteria".
   */
  type: string;
  /** Vergleichsoperator einer Zellwertregel, z. B. "GreaterThan". */
  operator: string | null;
  /** Vergleichswert oder Formel der Regel. */
  formula1: string | null;
  /** Formel einer benutzerdefinierten Regel. */
  customFormula: string | null;
  /** Suchtext einer Textregel. */
  text: string | null;
}

export interface ValidationInfo {
  sheetName: string;
  /** Geprüfter Bereich, z. B. "C2:C21". */
  address: string;
  /**
   * "None", "WholeNumber", "Decimal", "List", "Date", "Time", "TextLength",
   * "Custom" – oder "Inconsistent"/"MixedCriteria", wenn im Bereich
   * unterschiedliche Regeln liegen.
   */
  type: string;
  /** Quelle einer Dropdownliste, z. B. "=Kategorien" oder "Ja,Nein". */
  listSource: string | null;
  /** Wird die Liste als Dropdown in der Zelle angeboten? */
  inCellDropDown: boolean | null;
  /** Vergleichsoperator einer Zahlen- oder Datumsregel. */
  operator: string | null;
  /** Untere Grenze bzw. Vergleichswert. */
  formula1: string | null;
  /** Obere Grenze bei "zwischen". */
  formula2: string | null;
  /** Sind alle Werte im Bereich nach der Regel gültig? */
  valid: boolean | null;
}

export interface StyleInfo {
  name: string;
  /** Von Excel mitgeliefert oder selbst angelegt? */
  builtIn: boolean;
  numberFormat: string | null;
  bold: boolean | null;
  fontSize: number | null;
  fontColor: string | null;
  fillColor: string | null;
}

/** Formatangaben einer einzelnen Zelle, gelesen über `getCellProperties`. */
export interface CellFormatInfo {
  sheetName: string;
  address: string;
  /** Name der zugewiesenen Zellformatvorlage, z. B. "Überschrift 1". */
  style: string | null;
  bold: boolean | null;
  fontSize: number | null;
  fontColor: string | null;
  fillColor: string | null;
  /** "General", "Left", "Center", "Right", … */
  horizontalAlignment: string | null;
  wrapText: boolean | null;
}

/** Ein verbundener Zellblock, z. B. "B2:D2". */
export interface VerbundInfo {
  sheetName: string;
  address: string;
}

export interface FormatSnapshot {
  /** Alle bedingten Formatierungen. `null` vor ExcelApi 1.6. */
  conditionalFormats: ConditionalFormatInfo[] | null;
  /** Alle Zellformatvorlagen der Arbeitsmappe. `null` vor 1.7. */
  styles: StyleInfo[] | null;
  /** Datenüberprüfung je Spalte des benutzten Bereichs. `null` vor 1.8. */
  validations: ValidationInfo[] | null;
  /** Formatangaben je Zelle. `null` vor 1.9. */
  cellFormats: CellFormatInfo[] | null;
  /** Verbundene Zellblöcke. `null` vor 1.13. */
  mergedAreas: VerbundInfo[] | null;
}

function value<T>(read: () => T): T | null {
  try {
    const result = read();
    return result === undefined ? null : result;
  } catch {
    return null;
  }
}

function text(read: () => unknown): string | null {
  const result = value(read);
  if (typeof result === "string") return result;
  if (typeof result === "number" || typeof result === "boolean") return String(result);
  return null;
}

function flag(read: () => unknown): boolean | null {
  const result = value(read);
  return typeof result === "boolean" ? result : null;
}

function zahl(read: () => unknown): number | null {
  const result = value(read);
  return typeof result === "number" && Number.isFinite(result) ? result : null;
}

/**
 * Liest Regeln und Formatvorlagen der Arbeitsmappe.
 *
 * Jede Gruppe steckt in einem eigenen `try`: Scheitert die Datenüberprüfung,
 * bleiben bedingte Formatierung und Formatvorlagen trotzdem verfügbar.
 */
export async function readFormats(): Promise<FormatSnapshot> {
  if (!isExcelAvailable()) throw new ExcelUnavailableError();

  const canReadConditional = supportsExcelApi(excelApi.bedingteFormatierung);
  const canReadStyles = supportsExcelApi(excelApi.formatvorlagen);
  const canReadValidation = supportsExcelApi(excelApi.datenueberpruefung);
  const canReadCellProperties = supportsExcelApi(excelApi.zellformate);
  const canReadUsedRange = supportsExcelApi(excelApi.benutzterBereich);
  const canReadMerged = supportsExcelApi(excelApi.verbundeneZellen);

  return Excel.run(async (context) => {
    const sheetCollection = context.workbook.worksheets;
    sheetCollection.load("items/name");

    const styleCollection = canReadStyles ? context.workbook.styles : null;
    if (styleCollection) {
      styleCollection.load("items/name,items/builtIn,items/numberFormat");
    }
    await context.sync();

    const sheets = sheetCollection.items;

    // Benutzter Bereich je Blatt – Grundlage für alles Weitere.
    const usedRanges = new Map<string, Excel.Range>();
    if (canReadUsedRange) {
      for (const sheet of sheets) {
        const used = sheet.getUsedRangeOrNullObject(true);
        used.load("address,rowCount,columnCount,isNullObject");
        usedRanges.set(text(() => sheet.name) ?? "", used);
      }
    }

    // Schriftangaben der Formatvorlagen – eigener Ladeschritt, weil `font` und
    // `fill` Unterobjekte sind.
    if (styleCollection) {
      for (const style of styleCollection.items) {
        style.font.load("bold,size,color");
        style.fill.load("color");
      }
    }
    await context.sync();

    const styles: StyleInfo[] | null = styleCollection
      ? styleCollection.items.map((style) => ({
          name: text(() => style.name) ?? "",
          builtIn: flag(() => style.builtIn) ?? true,
          numberFormat: text(() => style.numberFormat),
          bold: flag(() => style.font.bold),
          fontSize: zahl(() => style.font.size),
          fontColor: text(() => style.font.color),
          fillColor: text(() => style.fill.color),
        }))
      : null;

    // Bedingte Formatierung, Datenüberprüfung und Zellformate hängen alle am
    // benutzten Bereich – deshalb erst jetzt, wo dessen Größe bekannt ist.
    const conditionalRequests: Array<{ sheetName: string; formats: Excel.ConditionalFormatCollection }> = [];
    const validationRequests: Array<{ sheetName: string; address: string; range: Excel.Range }> = [];
    const mergedRequests: Array<{ sheetName: string; bereiche: Excel.RangeAreas }> = [];
    const cellPropertyRequests: Array<{
      sheetName: string;
      ergebnis: OfficeExtension.ClientResult<Excel.CellProperties[][]>;
      startZeile: number;
      startSpalte: number;
    }> = [];

    for (const sheet of sheets) {
      const sheetName = text(() => sheet.name) ?? "";
      const used = usedRanges.get(sheetName);
      if (!used || value(() => used.isNullObject) === true) continue;

      const address = text(() => used.address);
      if (!address) continue;
      const bereich = splitAddress(address).range;
      const ecke = /^\$?([A-Z]+)\$?(\d+)/i.exec(bereich);
      if (!ecke) continue;

      let startSpalte = 0;
      for (const buchstabe of ecke[1].toUpperCase()) {
        startSpalte = startSpalte * 26 + (buchstabe.charCodeAt(0) - 64);
      }
      const startZeile = Number(ecke[2]);
      const zeilen = Math.min(value(() => used.rowCount) ?? 0, MAX_ZEILEN);
      const spalten = Math.min(value(() => used.columnCount) ?? 0, MAX_SPALTEN);
      if (zeilen <= 0 || spalten <= 0) continue;

      const von = `${columnLetter(startSpalte)}${startZeile}`;
      const bis = `${columnLetter(startSpalte + spalten - 1)}${startZeile + zeilen - 1}`;
      const gesamt = sheet.getRange(`${von}:${bis}`);

      if (canReadConditional) {
        const formats = gesamt.conditionalFormats;
        formats.load("items/type,items/id");
        conditionalRequests.push({ sheetName, formats });
      }

      if (canReadValidation) {
        // Spaltenweise: Eine Dropdownliste gilt fast immer für eine ganze
        // Spalte, und ein Abruf je Zelle wäre bei größeren Blättern zu teuer.
        for (let spalte = 0; spalte < spalten; spalte += 1) {
          const buchstabe = columnLetter(startSpalte + spalte);
          const spaltenBereich = `${buchstabe}${startZeile}:${buchstabe}${startZeile + zeilen - 1}`;
          const range = sheet.getRange(spaltenBereich);
          range.dataValidation.load("type,valid,rule");
          validationRequests.push({ sheetName, address: spaltenBereich, range });
        }
      }

      if (canReadMerged) {
        const verbunden = gesamt.getMergedAreasOrNullObject();
        verbunden.load("areas/items/address,isNullObject");
        mergedRequests.push({ sheetName, bereiche: verbunden });
      }

      if (canReadCellProperties) {
        cellPropertyRequests.push({
          sheetName,
          ergebnis: gesamt.getCellProperties({
            style: true,
            format: {
              font: { bold: true, size: true, color: true },
              fill: { color: true },
              horizontalAlignment: true,
              wrapText: true,
            },
          }),
          startZeile,
          startSpalte,
        });
      }
    }

    let gelesen = true;
    try {
      await context.sync();
    } catch {
      gelesen = false;
    }

    if (!gelesen) {
      return {
        conditionalFormats: null,
        styles,
        validations: null,
        cellFormats: null,
        mergedAreas: null,
      };
    }

    const mergedAreas: VerbundInfo[] | null = canReadMerged
      ? mergedRequests.flatMap((request) => {
          if (value(() => request.bereiche.isNullObject) === true) return [];
          const bereiche = value(() => request.bereiche.areas.items);
          if (!Array.isArray(bereiche)) return [];
          return bereiche.map((bereich) => ({
            sheetName: request.sheetName,
            address: splitAddress(text(() => bereich.address) ?? "").range,
          }));
        })
      : null;

    // Die Einzelheiten einer bedingten Regel stecken in Unterobjekten, die
    // erst geladen werden können, wenn die Regelart bekannt ist.
    const conditionalDetails: Array<{
      sheetName: string;
      type: string;
      range: Excel.Range;
      cellValue: Excel.CellValueConditionalFormat | null;
      custom: Excel.CustomConditionalFormat | null;
      textComparison: Excel.TextConditionalFormat | null;
    }> = [];

    for (const request of conditionalRequests) {
      for (const format of request.formats.items) {
        const type = text(() => format.type) ?? "Unbekannt";
        const range = format.getRangeOrNullObject();
        range.load("address,isNullObject");

        const cellValue = type === "CellValue" ? format.cellValueOrNullObject : null;
        if (cellValue) cellValue.load("rule");
        const custom = type === "Custom" ? format.customOrNullObject : null;
        if (custom) custom.load("rule");
        const textComparison = type === "ContainsText" ? format.textComparisonOrNullObject : null;
        if (textComparison) textComparison.load("rule");

        conditionalDetails.push({ sheetName: request.sheetName, type, range, cellValue, custom, textComparison });
      }
    }

    let regelDetailsGelesen = true;
    try {
      await context.sync();
    } catch {
      regelDetailsGelesen = false;
    }

    const conditionalFormats: ConditionalFormatInfo[] | null = canReadConditional
      ? conditionalDetails.map((eintrag) => {
          const { cellValue, custom, textComparison, range } = eintrag;
          const regel = cellValue ? value(() => cellValue.rule) : null;
          return {
            sheetName: eintrag.sheetName,
            address:
              regelDetailsGelesen && value(() => range.isNullObject) !== true
                ? splitAddress(text(() => range.address) ?? "").range || null
                : null,
            type: eintrag.type,
            operator: regel ? (text(() => regel.operator) ?? null) : null,
            formula1: regel ? (text(() => regel.formula1) ?? null) : null,
            customFormula: custom ? text(() => custom.rule.formula) : null,
            text: textComparison ? text(() => textComparison.rule.text) : null,
          };
        })
      : null;

    const validations: ValidationInfo[] | null = canReadValidation
      ? validationRequests.map((request) => {
          const validation = request.range.dataValidation;
          const regel = value(() => validation.rule);
          const liste = regel ? value(() => regel.list) : null;
          const zahlenRegel = regel
            ? (value(() => regel.wholeNumber) ?? value(() => regel.decimal) ?? value(() => regel.date) ?? null)
            : null;
          const quelle = liste ? value(() => liste.source) : null;

          return {
            sheetName: request.sheetName,
            address: request.address,
            type: text(() => validation.type) ?? "None",
            listSource: typeof quelle === "string" ? quelle : null,
            inCellDropDown: liste ? flag(() => liste.inCellDropDown) : null,
            operator: zahlenRegel ? text(() => zahlenRegel.operator) : null,
            formula1: zahlenRegel ? text(() => zahlenRegel.formula1) : null,
            formula2: zahlenRegel ? text(() => zahlenRegel.formula2) : null,
            valid: flag(() => validation.valid),
          };
        })
      : null;

    const cellFormats: CellFormatInfo[] | null = canReadCellProperties
      ? cellPropertyRequests.flatMap((request) => {
          const raster = value(() => request.ergebnis.value);
          if (!Array.isArray(raster)) return [];
          const treffer: CellFormatInfo[] = [];
          for (const [zeilenIndex, zeile] of raster.entries()) {
            if (!Array.isArray(zeile)) continue;
            for (const [spaltenIndex, zelle] of zeile.entries()) {
              treffer.push({
                sheetName: request.sheetName,
                address: `${columnLetter(request.startSpalte + spaltenIndex)}${request.startZeile + zeilenIndex}`,
                style: text(() => zelle.style),
                bold: flag(() => zelle.format?.font?.bold),
                fontSize: zahl(() => zelle.format?.font?.size),
                fontColor: text(() => zelle.format?.font?.color),
                fillColor: text(() => zelle.format?.fill?.color),
                horizontalAlignment: text(() => zelle.format?.horizontalAlignment),
                wrapText: flag(() => zelle.format?.wrapText),
              });
            }
          }
          return treffer;
        })
      : null;

    return { conditionalFormats, styles, validations, cellFormats, mergedAreas };
  });
}

/* ---------- Auswertung ---------- */

/** Selbst angelegte Zellformatvorlagen – also alles, was Excel nicht mitbringt. */
export function eigeneFormatvorlagen(snapshot: FormatSnapshot): StyleInfo[] {
  return (snapshot.styles ?? []).filter((style) => !style.builtIn);
}

/** Bedingte Formatierungen einer bestimmten Regelart. */
export function regelnVomTyp(snapshot: FormatSnapshot, type: string): ConditionalFormatInfo[] {
  return (snapshot.conditionalFormats ?? []).filter((regel) => regel.type === type);
}

/** Spalten, in denen eine Dropdownliste hinterlegt ist. */
export function dropdownSpalten(snapshot: FormatSnapshot): ValidationInfo[] {
  return (snapshot.validations ?? []).filter((eintrag) => eintrag.type === "List");
}

/** Spalten mit irgendeiner Datenüberprüfung, gleich welcher Art. */
export function gepruefteSpalten(snapshot: FormatSnapshot): ValidationInfo[] {
  return (snapshot.validations ?? []).filter(
    (eintrag) =>
      eintrag.type !== "None" &&
      eintrag.type !== "Inconsistent" &&
      eintrag.type !== "MixedCriteria",
  );
}

/** Zellen, denen eine bestimmte Zellformatvorlage zugewiesen ist. */
export function zellenMitVorlage(snapshot: FormatSnapshot, name: string): CellFormatInfo[] {
  const gesucht = name.trim().toLowerCase();
  return (snapshot.cellFormats ?? []).filter(
    (zelle) => (zelle.style ?? "").trim().toLowerCase() === gesucht,
  );
}

/**
 * Verbundene Zellblöcke, die mehr als eine Zelle umfassen.
 *
 * Ein Block wie "B2" wäre kein Verbund, sondern eine gewöhnliche Zelle – Excel
 * meldet solche Einzelzellen gelegentlich mit.
 */
export function echteVerbuende(snapshot: FormatSnapshot): VerbundInfo[] {
  return (snapshot.mergedAreas ?? []).filter((eintrag) => eintrag.address.includes(":"));
}

/** Zellen mit einer zugewiesenen Zellformatvorlage, gleich welcher. */
export function zellenMitIrgendeinerVorlage(snapshot: FormatSnapshot): CellFormatInfo[] {
  return (snapshot.cellFormats ?? []).filter((zelle) => {
    const name = (zelle.style ?? "").trim();
    // "Standard"/"Normal" ist die Grundvorlage jeder Zelle und zählt nicht als
    // bewusste Zuweisung.
    return name.length > 0 && !/^(standard|normal)$/i.test(name);
  });
}
